import React from "react";
import { makeStyles, tokens } from "@fluentui/react-components";
import { LockClosed24Regular, Calculator24Regular, Info24Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  pageWrapper: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: "400px",
    overflow: "hidden",
  },
  blurredContent: {
    filter: "blur(2px)",
    pointerEvents: "none",
    userSelect: "none",
    opacity: 0.25,
    width: "100%",
    height: "100%",
  },
  overlay: {
    position: "absolute",
    inset: 0,
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  },
  card: {
    width: "340px",
    background: "rgba(255, 255, 255, 0.55)",
    backdropFilter: "blur(24px)",
    WebkitBackdropFilter: "blur(24px)",
    border: "1px solid rgba(255, 255, 255, 0.75)",
    borderRadius: "10px",
    padding: "32px 28px 26px",
    boxShadow: [
      "0 8px 32px rgba(99, 102, 241, 0.12)",
      "0 2px 8px rgba(0, 0, 0, 0.06)",
      "inset 0 1px 0 rgba(255, 255, 255, 0.9)",
    ].join(", "),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    overflow: "hidden",
  },
  cardShimmer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)",
  },
  lockRing: {
    width: "56px",
    height: "56px",
    borderRadius: "10px",
    background: "rgba(99, 102, 241, 0.1)",
    border: "1px solid rgba(99, 102, 241, 0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    flexShrink: 0,
  },
  title: {
    fontSize: "17px",
    fontWeight: tokens.fontWeightSemibold,
    color: "#1e1b4b",
    textAlign: "center",
    margin: "0 0 8px",
  },
  subtitle: {
    fontSize: "13px",
    color: "#4b5563",
    textAlign: "center",
    lineHeight: "1.65",
    margin: "0 0 20px",
  },
  modulePill: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(254, 243, 199, 0.7)",
    border: "1px solid rgba(253, 230, 138, 0.8)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    borderRadius: "30px",
    padding: "5px 14px",
    marginBottom: "24px",
    fontSize: "12px",
    fontWeight: tokens.fontWeightSemibold,
    color: "#92400e",
  },
  divider: {
    height: "1px",
    background: "rgba(99, 102, 241, 0.1)",
    width: "100%",
    marginBottom: "20px",
  },
  actions: {
    display: "flex",
    gap: "10px",
    width: "100%",
    marginBottom: "18px",
  },
  btnUpgrade: {
    flex: 1,
    background: "rgba(99, 102, 241, 0.85)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid rgba(99, 102, 241, 0.5)",
    borderRadius: "10px",
    padding: "10px",
    fontSize: "13px",
    fontWeight: tokens.fontWeightSemibold,
    color: "#ffffff",
    cursor: "pointer",
    ":hover": {
      background: "rgba(79, 70, 229, 0.9)",
    },
  },
  btnBack: {
    flex: 1,
    background: "rgba(255, 255, 255, 0.5)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    border: "1px solid rgba(255, 255, 255, 0.7)",
    borderRadius: "10px",
    padding: "10px",
    fontSize: "13px",
    fontWeight: tokens.fontWeightSemibold,
    color: "#374151",
    cursor: "pointer",
    ":hover": {
      background: "rgba(255, 255, 255, 0.75)",
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "11px",
    color: "#6b7280",
    margin: 0,
  },
});

interface SubscriptionGuardProps {
  moduleName: string;
  isSubscribed: boolean;
  onUpgrade?: () => void;
  onGoBack?: () => void;
  adminContact?: string;
  children: React.ReactNode;
}

const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  moduleName,
  isSubscribed,
  onUpgrade,
  onGoBack,
  adminContact,
  children,
}) => {
  const styles = useStyles();

  if (isSubscribed) {
    return <>{children}</>;
  }

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.blurredContent} aria-hidden="true">
        {children}
      </div>

      <div
        className={styles.overlay}
        role="alertdialog"
        aria-modal="true"
        aria-label="Module not subscribed"
      >
        <div className={styles.card}>
          <div className={styles.cardShimmer} aria-hidden="true" />

          <div className={styles.lockRing} aria-hidden="true">
            <LockClosed24Regular style={{ fontSize: "24px", color: "#6366f1" }} />
          </div>

          <p className={styles.title}>Module not subscribed</p>
          <p className={styles.subtitle}>
            Your current plan does not include
            <br />
            access to this module.
          </p>

          <div className={styles.modulePill}>
            <Calculator24Regular style={{ fontSize: "13px", color: "#d97706" }} aria-hidden="true" />
            {moduleName}
          </div>

          <div className={styles.divider} />

          <div className={styles.actions}>
            <button className={styles.btnUpgrade} onClick={onUpgrade}>
              Upgrade plan
            </button>
            <button className={styles.btnBack} onClick={onGoBack}>
              Go back
            </button>
          </div>

          <p className={styles.footer}>
            <Info24Regular style={{ fontSize: "13px", color: "#9ca3af" }} aria-hidden="true" />
            {adminContact
              ? `Contact ${adminContact} to enable this module`
              : "Contact your admin to enable this module"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionGuard;